import React, { memo } from "react";
import { HiClock, HiUsers, HiOutlineArrowLeft } from "react-icons/hi";
import { HiTicket } from "react-icons/hi2";
import { StatusBadge, BadgeTone } from "../StatusBadge";
import moment from "moment";
import { TicketTypeWithId } from "../../../hooks/useEventCardState";

interface TicketCardProps {
  ticket: TicketTypeWithId;
  isSelected: boolean;
  remaining: number | null;
  onSelect: (ticketId: string) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}

const statusBadgeConfig: Record<
  string,
  { color: BadgeTone; className: string; key: string }
> = {
  sold_out: {
    color: "failure",
    className: "bg-red-500 text-white",
    key: "event.tickets.soldOut",
  },
  coming_soon: {
    color: "warning",
    className: "bg-amber-500 text-white",
    key: "event.tickets.comingSoon",
  },
  sale_ended: {
    color: "gray",
    className: "bg-gray-500 text-white",
    key: "event.tickets.saleEnded",
  },
};

const TicketCard: React.FC<TicketCardProps> = memo(
  ({ ticket, isSelected, remaining, onSelect, t }) => {
    const isDisabled = ticket.status !== "available";
    const showRemaining =
      !isDisabled &&
      ticket.settings.show_remaining_tickets !== false &&
      remaining !== null;

    const handleClick = () => {
      if (!isDisabled) onSelect(ticket.ticket_id);
    };

    const badgeConfig = isDisabled
      ? statusBadgeConfig[ticket.status]
      : undefined;

    return (
      <div
        onClick={handleClick}
        className={`flex items-center justify-between rounded-xl border transition-all duration-200 px-2 py-3 ${
          isDisabled
            ? "bg-black/25 border-white/10 opacity-50 cursor-not-allowed"
            : isSelected
            ? "bg-white/20 border-white/30 scale-[1.02] cursor-pointer"
            : "bg-black/25 border-white/15 hover:scale-[1.02] hover:bg-black/35 cursor-pointer"
        }`}
        dir="auto"
      >
        <div className="flex items-center gap-1 flex-1 min-w-0">
          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white flex items-center justify-center shadow-sm">
            <HiTicket className="text-gray-600" size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-white truncate">
                {ticket.settings.name}
              </span>
              {badgeConfig ? (
                <StatusBadge
                  size="sm"
                  color={badgeConfig.color}
                  className={`${badgeConfig.className} flex-shrink-0`}
                >
                  {t(badgeConfig.key)}
                </StatusBadge>
              ) : ticket.settings.price === 0 ? (
                <StatusBadge
                  color="success"
                  size="sm"
                  emphasis="solid"
                  className="flex-shrink-0"
                >
                  {t("event.tickets.free")}
                </StatusBadge>
              ) : (
                <span className="text-xs font-bold text-white flex-shrink-0">
                  {ticket.settings.price} {ticket.settings.currency}
                </span>
              )}
            </div>
            {ticket.settings.description && (
              <p className="text-xs text-white/60 truncate mt-0.5">
                {ticket.settings.description}
              </p>
            )}
            {(showRemaining ||
              (!isDisabled && ticket.settings.sale_end_date)) && (
              <div className="flex flex-col gap-1 text-xs text-white/60 mt-1">
                {showRemaining && (
                  <div className="flex items-center gap-1">
                    <HiUsers size={12} />
                    <span>
                      {remaining} {t("event.tickets.left")}
                    </span>
                  </div>
                )}
                {!isDisabled && ticket.settings.sale_end_date && (
                  <div className="flex items-center gap-1">
                    <HiClock size={12} />
                    <span>
                      {t("event.tickets.ends")}{" "}
                      {moment(ticket.settings.sale_end_date).format(
                        "MMM D , HH:mm"
                      )}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {!isDisabled && (
          <HiOutlineArrowLeft className="w-4 h-4 rotate-180 flex-shrink-0 ml-2 text-white" />
        )}
      </div>
    );
  }
);

TicketCard.displayName = "TicketCard";

export default TicketCard;
